import { Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { ChangelogEntry } from '@/assets/changelogs';

export interface ChangelogItemProps {
  changelog: ChangelogEntry;
}

export const ChangelogItem = ({ changelog }: ChangelogItemProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="bg-muted/20 rounded-lg border border-border/50 p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full border border-primary/20">
          <Calendar className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-medium text-primary">{formatDate(changelog.date)}</span>
        </div>
      </div>

      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            h1: ({ children }) => (
              <h1 className="text-xl font-bold text-foreground mb-3 leading-tight">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-lg font-semibold text-foreground mb-2 mt-4 leading-tight">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-base font-medium text-foreground mb-2 mt-3 leading-tight">
                {children}
              </h3>
            ),
            p: ({ children }) => (
              <p className="text-foreground/90 mb-3 leading-relaxed text-sm">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc list-inside mb-3 space-y-1 ml-2">{children}</ul>
            ),
            li: ({ children }) => (
              <li className="text-foreground/90 text-sm leading-relaxed">{children}</li>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">{children}</strong>
            ),
            em: ({ children }) => <em className="italic text-muted-foreground">{children}</em>,
            a: ({ href, children }) => (
              <a
                href={href}
                className="text-primary hover:text-primary/80 underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            ),
            hr: () => <hr className="border-border/50 my-4" />,
            img: ({ src, alt }) => (
              <img
                src={src}
                alt={alt}
                className="rounded-lg max-w-full h-auto my-3 border border-border/30"
                loading="lazy"
              />
            ),
            code: ({ children }) => (
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
            ),
            pre: ({ children }) => (
              <pre className="bg-muted p-3 rounded-lg overflow-x-auto text-xs border border-border/30 my-3">
                {children}
              </pre>
            ),
            video: ({ src: _src, children, ...props }) => (
              <video
                {...props}
                className="rounded-lg max-w-full h-auto my-4 border border-border/30"
                style={{ maxWidth: '800px', width: '100%' }}
              >
                {children}
              </video>
            ),
          }}
        >
          {changelog.content}
        </ReactMarkdown>
      </div>
    </div>
  );
};
